import { formatWeight } from '@generated/utils/units';
import { formatCurrencyWhole } from '@generated/utils/format';
import { annualCostLabel, formatNoteText, periodCostLabel } from '@generated/utils/pricing';
import type { Commitment, FormatItem, Frequency, Unit } from '@generated/types';

interface SummaryPanelProps {
  monthlyWeightLbs: number;
  termWeightLbs: number;
  unit: Unit;
  frequency: Frequency | null;
  commitment: Commitment | null;
  periodCost: number | null;
  annualCost: number | null;
  selectedFormat: FormatItem | undefined;
}

export function SummaryPanel({
  monthlyWeightLbs,
  termWeightLbs,
  unit,
  frequency,
  commitment,
  periodCost,
  annualCost,
  selectedFormat,
}: SummaryPanelProps) {
  const isHandling = selectedFormat?.handlingPerUnit != null;
  const periodLabel = frequency ? periodCostLabel(frequency, isHandling) : null;
  const showPeriod = periodLabel != null;

  const noteText = formatNoteText(selectedFormat);
  const termNote = commitment === 'Month-to-Month' ? ' Term total shows one month.' : '';

  return (
    <div className="mt-5 rounded-xl border border-foreground/10 bg-foreground p-6 text-background sm:p-8">
      <h3 className="font-[family-name:var(--font-heading)] text-sm font-medium tracking-wide text-background/70">
        Your reservation
      </h3>
      <div className={`tnum mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 ${showPeriod ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <div>
          <div className="text-xs text-background/60">Monthly volume ({unit})</div>
          <div className="mt-1 text-lg font-medium">
            {formatWeight(monthlyWeightLbs, unit)} {unit}
          </div>
        </div>
        <div>
          <div className="text-xs text-background/60">Over the term ({unit})</div>
          <div className="mt-1 text-lg font-medium">
            {formatWeight(termWeightLbs, unit)} {unit}
          </div>
        </div>
        {showPeriod ? (
          <div>
            <div className="text-xs text-background/60">{periodLabel}</div>
            <div className="mt-1 text-lg font-medium">{formatCurrencyWhole(periodCost)}</div>
          </div>
        ) : null}
        <div>
          <div className="text-xs text-background/60">{annualCostLabel(isHandling)}</div>
          <div className="mt-1 text-lg font-medium">{formatCurrencyWhole(annualCost)}</div>
        </div>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-background/60">
        {noteText ? `${noteText} ` : ''}
        Estimates are based on our published rate card and are for planning only. Final pricing is confirmed in the
        service agreement.{termNote}
      </p>
    </div>
  );
}
